import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pagination engine for Preach Mode's Book mode. See docs/plan_preach_book_mode.md §2.
 *
 * The content is fragmented by CSS multi-column inside a fixed-height flow; overflow
 * columns extend horizontally and the viewport scrolls them with native
 * `scroll-snap-type: x mandatory`. This hook owns everything derived from that layout:
 * geometry, page count, navigation, position restore across repagination, and the
 * observers that decide when repagination is allowed to happen at all.
 *
 * Invariant this hook exists to protect (§2.4): after first paint, only user-initiated
 * changes (font, margin, columns — signalled via `remeasureToken`) and device rotation
 * may repaginate. A preacher reads from this mid-sermon; an uninvited reflow is a
 * place-loss event. Hence:
 *  - the page area is pixel-locked to `stageHeight` and only re-synced through the
 *    filtered path below, so iOS chrome jitter cannot reflow the columns;
 *  - the ResizeObserver watches a separate fixed probe (never the elements being
 *    measured), debounces 120ms, and ignores height deltas under 40px;
 *  - `document.fonts.ready` and image `decode()` settle before `ready` flips true,
 *    and images are forced `loading="eager"` so a lazy image can never reflow later;
 *  - `orientationchange` is deliberately not used — it fires before iOS updates the
 *    viewport dimensions; the probe's ResizeObserver covers rotation.
 *
 * Spike verdicts binding on this implementation (§0.2, device runs 2026-07-31→08-01):
 *  1. Single column MUST be `column-width` (≥ container width), never `column-count: 1`
 *     — WebKit creates no overflow columns for count:1, so there is nothing to swipe.
 *  2. Page count comes from a zero-size sentinel's getBoundingClientRect(), not
 *     scrollWidth (integer-rounded and engine-divergent over 40+ columns).
 *  5. Page-sized snap areas AND the JS settle-correction ship together permanently —
 *     1px snap targets under-snapped on real-device momentum flicks (half pages).
 */

const DEFAULT_GAP = 48;

/**
 * `break-inside: avoid` on a unit taller than this fraction of a column would
 * strand an equally tall void at the previous column's bottom when it jumps whole
 * to the next page — inside a tinted section card that reads as a rendering bug.
 * Units above the threshold get `data-book-tall` and are allowed to split at a
 * line box; everything below keeps `avoid` and never breaks mid-sentence.
 */
const TALL_SPLIT_FRACTION = 0.35;

export interface UsePagedReaderOptions {
  /** Requested column count. The hook clamps 2 → 1 when a column would fall below
   *  `minColumnWidth` — a safety floor (phone portrait, 220% font), not a resolver. */
  columns: 1 | 2;
  /** Column gap in px. Also the visual seam between pages of a 1-column layout. */
  gap?: number;
  /** Minimum usable column width in px before a 2-column request clamps to 1. */
  minColumnWidth?: number;
  /**
   * Fingerprint of every user setting that changes text metrics (font size, font
   * family, margin width, top offset). Any change repaginates with position restore.
   */
  remeasureToken?: string;
}

export interface PagedReader {
  viewportRef: React.RefObject<HTMLDivElement>;
  flowRef: React.RefObject<HTMLDivElement>;
  sentinelRef: React.RefObject<HTMLSpanElement>;
  /** Fixed, invisible, observed by the ResizeObserver. Must span the exact area the
   *  stage should occupy (`top: topOffset; bottom: 0; left: 0; right: 0`). */
  probeRef: React.RefObject<HTMLDivElement>;
  pageIndex: number;
  pageCount: number;
  /** Scroll distance of one page turn: columns × (colW + gap). 0 until measured. */
  stride: number;
  gap: number;
  /** Effective column count after the safety clamp. */
  columns: 1 | 2;
  /** Pixel-locked height for the stage element. 0 until the probe is first read
   *  (render `bottom: 0` as the fallback). */
  stageHeight: number;
  /** Fonts and images have settled and the first measurement is done. Keep the
   *  reader hidden until this is true — showing it earlier paints a layout that is
   *  still going to shift (§2.4). */
  ready: boolean;
  goTo: (page: number) => void;
  next: () => void;
  prev: () => void;
  /** Jump to the page containing a `data-block-index` anchor. */
  goToBlock: (blockIndex: number) => void;
  /** Column styles for the flow element, including `--page-h` for the image cap.
   *  Built here so verdict 1 (column-width, never column-count: 1) has one home. */
  flowStyle: React.CSSProperties;
}

/** `behavior: 'smooth'` is JS-driven motion, unaffected by the CSS
 *  prefers-reduced-motion block — so honor the preference here (§4.5). */
function scrollBehavior(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

export function usePagedReader({
  columns: requestedColumns,
  gap = DEFAULT_GAP,
  minColumnWidth = 0,
  remeasureToken = '',
}: UsePagedReaderOptions): PagedReader {
  const viewportRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);

  const [effectiveCols, setEffectiveCols] = useState<1 | 2>(() => requestedColumns);
  const [stageHeight, setStageHeight] = useState(0);
  const [pageH, setPageH] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [stride, setStride] = useState(0);
  const [ready, setReady] = useState(false);
  const [resizeTick, setResizeTick] = useState(0);

  // Geometry the event handlers need without re-registering listeners.
  const metricsRef = useRef({ colW: 0, stride: 0, pageCount: 1, cols: 1 as 1 | 2 });
  const pageIndexRef = useRef(0);
  const hasMeasuredRef = useRef(false);
  // Block index the reader was on at the last stable moment, and whether the next
  // completed measurement should scroll back to it (§2.5).
  const anchorRef = useRef(0);
  const restorePendingRef = useRef(false);

  const setPage = useCallback((page: number) => {
    pageIndexRef.current = page;
    setPageIndex(page);
  }, []);

  /**
   * Record the first block at/after the viewport's left edge. Anchoring on block
   * index rather than section id means sermons with no section cards still hold
   * their place. Called from the settle path, so the anchor always reflects the
   * last layout the reader actually looked at — a font-size change has already
   * reflowed the DOM by the time any effect can run, so capturing "just before"
   * a change is impossible; capturing at every rest is the reliable substitute.
   */
  const captureAnchor = useCallback(() => {
    const vp = viewportRef.current;
    const flow = flowRef.current;
    if (!vp || !flow) return;
    const edge = vp.getBoundingClientRect().left;
    const blocks = Array.from(flow.querySelectorAll<HTMLElement>('[data-block-index]'));
    const first = blocks.find((b) => b.getBoundingClientRect().left >= edge - 2);
    if (first?.dataset.blockIndex) anchorRef.current = Number(first.dataset.blockIndex);
  }, []);

  const measure = useCallback(() => {
    const vp = viewportRef.current;
    const flow = flowRef.current;
    const sentinel = sentinelRef.current;
    if (!vp || !flow || !sentinel) return;

    // The FLOW's content width, not vp.clientWidth — clientWidth includes the
    // viewport's horizontal padding, which the multicol engine does not lay out into.
    const flowW = flow.clientWidth;
    if (flowW <= 0) return;

    // The clamp depends only on flowW, which column CSS does not affect, so this
    // cannot oscillate: one extra render pass at most, then the numbers agree.
    const wantCols: 1 | 2 =
      requestedColumns === 2 && (flowW - gap) / 2 >= minColumnWidth ? 2 : 1;
    if (wantCols !== effectiveCols) {
      setEffectiveCols(wantCols);
      return; // the measuring effect re-runs once the new column CSS is committed
    }

    const colW = (flowW - gap * (wantCols - 1)) / wantCols;
    const colStep = colW + gap;
    const strideNow = wantCols * colStep;

    // Scroll-independent: both rects shift together as the scroller moves. The +1
    // absorbs sub-pixel error; a disagreement of exactly one column with scrollWidth
    // is the sentinel landing in a fresh trailing column (spike Q4), not a defect.
    const flowLeft = flow.getBoundingClientRect().left;
    const sentX = sentinel.getBoundingClientRect().left - flowLeft;
    const totalCols = Math.max(1, Math.floor((sentX + 1) / colStep) + 1);
    const count = Math.max(1, Math.ceil(totalCols / wantCols));

    metricsRef.current = { colW, stride: strideNow, pageCount: count, cols: wantCols };
    setStride(strideNow);
    setPageCount(count);

    if (restorePendingRef.current) {
      restorePendingRef.current = false;
      const el = flow.querySelector<HTMLElement>(`[data-block-index="${anchorRef.current}"]`);
      if (el) {
        const x = el.getBoundingClientRect().left - flowLeft;
        const page = Math.min(count - 1, Math.max(0, Math.floor(Math.floor((x + 1) / colStep) / wantCols)));
        vp.scrollTo({ left: page * strideNow, behavior: 'auto' });
        setPage(page);
      } else {
        setPage(Math.min(pageIndexRef.current, count - 1));
      }
    } else {
      setPage(Math.min(pageIndexRef.current, count - 1));
    }

    hasMeasuredRef.current = true;
    setReady(true);
  }, [requestedColumns, gap, minColumnWidth, effectiveCols, setPage]);

  // Rotation / real viewport changes. The probe is a separate fixed element:
  // observing an element this hook mutates produces "ResizeObserver loop completed
  // with undelivered notifications" and dropped frames (§2.4).
  useEffect(() => {
    const probe = probeRef.current;
    if (!probe) return;
    const last = { w: 0, h: 0 };
    const ro = new ResizeObserver((entries) => {
      const r = entries[entries.length - 1].contentRect;
      const first = last.w === 0 && last.h === 0;
      const dw = Math.abs(r.width - last.w);
      const dh = Math.abs(r.height - last.h);
      // iOS Safari's chrome shift is a sub-40px height twitch; repaginating on it
      // would reflow mid-sentence. Width changes always count (rotation, Split View).
      if (!first && dw < 1 && dh < 40) return;
      last.w = r.width;
      last.h = r.height;
      // The initial delivery is the mount-time baseline the measuring effect
      // already handles; only subsequent real changes need to force a pass.
      if (!first) setResizeTick((t) => t + 1);
    });
    ro.observe(probe);
    return () => ro.disconnect();
  }, []);

  // The measuring pipeline. Multi-pass by design — each pass commits one layout
  // input, returns, and re-runs via its own dependency, so every read below happens
  // against a fully committed layout:
  //   (1) sync the stage's pixel-locked height from the probe,
  //   (2) sync --page-h (image max-height cap) from the flow,
  //   (3) settle fonts + images, then measure after a double-rAF.
  useEffect(() => {
    const flow = flowRef.current;
    const probe = probeRef.current;
    if (!flow || !probe) return;

    let cancelled = false;
    const delay = hasMeasuredRef.current ? 120 : 0; // debounce reflows, not first paint

    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (hasMeasuredRef.current) restorePendingRef.current = true;

      // (1) Pixel-lock the page area. Between syncs, chrome jitter the observer
      // filtered out cannot change the fragmentainer height under the reader.
      const target = Math.round(probe.getBoundingClientRect().height);
      if (target > 0 && Math.abs(target - stageHeight) >= 1) {
        setStageHeight(target);
        return;
      }

      // (2) --page-h must be final BEFORE images are decoded and metrics read —
      // an image capped at the 100vh fallback would shrink after measurement and
      // silently invalidate the page count. Never 0: 0 would collapse every image.
      const h = flow.clientHeight;
      if (h > 0 && Math.abs(h - pageH) >= 1) {
        setPageH(h);
        return;
      }

      void (async () => {
        // (3) DM Serif Display swaps in async and shifts every metric; a lazy image
        // decoding later would reflow content behind the reader. Both settle now.
        await document.fonts.ready;
        const imgs = Array.from(flow.querySelectorAll('img'));
        for (const img of imgs) {
          if (img.loading !== 'eager') img.loading = 'eager';
        }
        await Promise.all(imgs.map((img) => img.decode().catch(() => undefined)));
        if (cancelled) return;

        // (4) Tag units too tall for `break-inside: avoid` (see TALL_SPLIT_FRACTION).
        // Heights depend on font/margin/column width — all settled by now — and the
        // attribute changes fragmentation only, never element heights, so tagging
        // before the measure cannot invalidate what the measure reads.
        const colH = flow.clientHeight;
        if (colH > 0) {
          for (const el of Array.from(flow.querySelectorAll<HTMLElement>('p, li, blockquote'))) {
            if (el.offsetHeight > colH * TALL_SPLIT_FRACTION) el.setAttribute('data-book-tall', '');
            else el.removeAttribute('data-book-tall');
          }
        }

        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (!cancelled) measure();
          })
        );
      })();
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [measure, remeasureToken, effectiveCols, resizeTick, stageHeight, pageH]);

  const goTo = useCallback(
    (page: number) => {
      const vp = viewportRef.current;
      const m = metricsRef.current;
      if (!vp || m.stride <= 0) return;
      const clamped = Math.max(0, Math.min(page, m.pageCount - 1));
      vp.scrollTo({ left: clamped * m.stride, behavior: scrollBehavior() });
    },
    []
  );

  const next = useCallback(() => goTo(pageIndexRef.current + 1), [goTo]);
  const prev = useCallback(() => goTo(pageIndexRef.current - 1), [goTo]);

  const goToBlock = useCallback(
    (blockIndex: number) => {
      const vp = viewportRef.current;
      const flow = flowRef.current;
      const m = metricsRef.current;
      if (!vp || !flow || m.stride <= 0) return;
      const el = flow.querySelector<HTMLElement>(`[data-block-index="${blockIndex}"]`);
      if (!el) return;
      const x = el.getBoundingClientRect().left - flow.getBoundingClientRect().left;
      const colStep = m.colW + gap;
      const page = Math.min(m.pageCount - 1, Math.max(0, Math.floor(Math.floor((x + 1) / colStep) / m.cols)));
      vp.scrollTo({ left: page * m.stride, behavior: scrollBehavior() });
    },
    [gap]
  );

  // Scroll tracking + settle-correction. iOS momentum occasionally comes to rest
  // between snap points even with page-sized snap areas (seen on device, mostly on
  // back swipes) — 150ms after the last scroll event, with no finger down, nudge to
  // the nearest stride multiple. Ships permanently per spike verdict 5. The settled
  // rest position is also where the restore anchor is captured.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    let timer: number | undefined;
    let touching = false;

    const settle = () => {
      if (touching) return;
      const m = metricsRef.current;
      if (m.stride <= 0) return;
      const nearest = Math.round(vp.scrollLeft / m.stride) * m.stride;
      if (Math.abs(vp.scrollLeft - nearest) > 2) {
        vp.scrollTo({ left: nearest, behavior: scrollBehavior() });
        // The nudge emits scroll events of its own; the anchor is captured on the
        // aligned settle that follows.
        return;
      }
      captureAnchor();
    };
    const onScroll = () => {
      const m = metricsRef.current;
      if (m.stride > 0) {
        const page = Math.max(0, Math.min(m.pageCount - 1, Math.round(vp.scrollLeft / m.stride)));
        if (page !== pageIndexRef.current) setPage(page);
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, 150);
    };
    // A resting finger emits no scroll events — without this guard the timer would
    // yank the page out from under a paused drag.
    const onTouchStart = () => {
      touching = true;
      window.clearTimeout(timer);
    };
    const onTouchEnd = () => {
      touching = false;
      timer = window.setTimeout(settle, 150);
    };

    vp.addEventListener('scroll', onScroll, { passive: true });
    vp.addEventListener('touchstart', onTouchStart, { passive: true });
    vp.addEventListener('touchend', onTouchEnd, { passive: true });
    vp.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.clearTimeout(timer);
      vp.removeEventListener('scroll', onScroll);
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchend', onTouchEnd);
      vp.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [captureAnchor, setPage]);

  const flowStyle: React.CSSProperties = {
    height: '100%',
    columnGap: `${gap}px`,
    columnFill: 'auto',
    // Verdict 1: WebKit creates no overflow columns for `column-count: 1` — content
    // overflows vertically and there is nothing to swipe. A column-width at least as
    // wide as the flow clamps to exactly one full-width column via a code path that
    // fragments correctly.
    columnCount: effectiveCols === 2 ? 2 : 'auto',
    columnWidth: effectiveCols === 2 ? 'auto' : '100vw',
    ...(pageH > 0 ? ({ '--page-h': `${pageH}px` } as React.CSSProperties) : null),
  };

  return {
    viewportRef,
    flowRef,
    sentinelRef,
    probeRef,
    pageIndex,
    pageCount,
    stride,
    gap,
    columns: effectiveCols,
    stageHeight,
    ready,
    goTo,
    next,
    prev,
    goToBlock,
    flowStyle,
  };
}
